import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "smrtlyfit_thread_last_read";

interface ThreadUnreadInfo {
  threadId: string;
  lastMessageAt: string | null;
  lastSenderId: string | null;
}

function getLastReadMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function setLastRead(threadId: string) {
  const map = getLastReadMap();
  map[threadId] = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

/**
 * Tracks unread messages across all threads the user participates in.
 * Uses localStorage to store per-thread "last read" timestamps.
 * Returns the set of unread thread IDs, a total count, and a mark-read function.
 */
export function useUnreadMessages() {
  const { user } = useAuth();
  const [unreadThreadIds, setUnreadThreadIds] = useState<Set<string>>(new Set());
  const [unreadFriendIds, setUnreadFriendIds] = useState<Set<string>>(new Set());
  const [unreadCoachIds, setUnreadCoachIds] = useState<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkUnread = useCallback(async () => {
    if (!user) return;

    // Fetch threads the user is part of (coach + friend threads)
    const { data: threads, error } = await supabase
      .from("message_threads")
      .select("id, last_message_at, client_id, coach_id, thread_type")
      .or(`client_id.eq.${user.id},coach_id.eq.${user.id}`);

    if (error || !threads) return;

    const threadIds = threads.filter((t) => t.last_message_at).map((t) => t.id);
    if (threadIds.length === 0) {
      setUnreadThreadIds(new Set());
      setUnreadFriendIds(new Set());
      setUnreadCoachIds(new Set());
      return;
    }

    // Fetch the latest message per thread to check sender
    const { data: latestMessages } = await supabase
      .from("messages")
      .select("thread_id, sender_id, created_at")
      .in("thread_id", threadIds)
      .order("created_at", { ascending: false });

    // Build map: threadId -> latest message info (only the first/latest per thread)
    const latestByThread = new Map<string, { sender_id: string; created_at: string }>();
    (latestMessages || []).forEach((m: any) => {
      if (!latestByThread.has(m.thread_id)) {
        latestByThread.set(m.thread_id, { sender_id: m.sender_id, created_at: m.created_at });
      }
    });

    // Build thread type map
    const threadTypeMap = new Map<string, string>();
    threads.forEach((t: any) => threadTypeMap.set(t.id, t.thread_type || "coaching"));

    const lastReadMap = getLastReadMap();
    const unread = new Set<string>();
    const friendUnread = new Set<string>();
    const coachUnread = new Set<string>();

    for (const thread of threads) {
      const latest = latestByThread.get(thread.id);
      if (!latest) continue;

      // Only mark as unread if the latest message is NOT from the current user
      if (latest.sender_id === user.id) continue;

      const lastRead = lastReadMap[thread.id];
      if (!lastRead || new Date(latest.created_at) > new Date(lastRead)) {
        unread.add(thread.id);
        const tType = threadTypeMap.get(thread.id);
        if (tType === "friend") {
          friendUnread.add(thread.id);
        } else {
          coachUnread.add(thread.id);
        }
      }
    }

    setUnreadThreadIds(unread);
    setUnreadFriendIds(friendUnread);
    setUnreadCoachIds(coachUnread);
  }, [user]);

  useEffect(() => {
    checkUnread();

    // Poll every 10s for unread status
    intervalRef.current = setInterval(checkUnread, 10000);

    // Also listen for custom event to refresh immediately
    const handler = () => checkUnread();
    window.addEventListener("chat-read-update", handler);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("chat-read-update", handler);
    };
  }, [checkUnread]);

  const markThreadRead = useCallback((threadId: string) => {
    setLastRead(threadId);
    setUnreadThreadIds((prev) => {
      const next = new Set(prev);
      next.delete(threadId);
      return next;
    });
    // Notify other instances of the hook
    window.dispatchEvent(new Event("chat-read-update"));
  }, []);

  return {
    unreadThreadIds,
    totalUnread: unreadThreadIds.size,
    hasUnread: unreadThreadIds.size > 0,
    hasUnreadFriend: unreadFriendIds.size > 0,
    hasUnreadCoach: unreadCoachIds.size > 0,
    markThreadRead,
    refresh: checkUnread,
  };
}
